// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import {
  assembleQuery,
  deleteWrapper,
  fetchWrapper,
  patchWrapper,
  postFormDataWrapper,
  postWrapper,
  putWrapper,
} from 'helpers/api'

export const copyPathsIntoProject = (projectId, pathIds) => {
  const query = [{
    field: 'pathIds',
    value: pathIds.join(','),
  }]
  return postWrapper(`/projects/${projectId}/copy-paths${assembleQuery(query)}`)
}

// projectIds - an array of projectIds to be deleted
export const deleteProjectsById = projectIds => {
  const query = [{
    field: 'projectDeletes',
    value: projectIds.join(','),
  }]
  return deleteWrapper(`/projects${assembleQuery(query)}`)
}

export const deleteProjectsFile = (projectId, projectFileId) => (
  deleteWrapper(`/projects/${projectId}/files/${projectFileId}`)
)

// adding a query automatically gives the request
// a ?expires={Date.now()} so can guarantee that project data
// is always fresh and the browser isn't using cached values.
export const fetchProject = p => fetchWrapper(`/projects/${p}${assembleQuery()}`)

export const fetchProjectAFSFilters = (projectId, query = '') => (
  fetchWrapper(`/projects/${projectId}/afs-filters${query}`)
)

export const fetchProjectAnalysisAFSGrid = (projectId, query = '') => (
  fetchWrapper(`/projects/${projectId}/analysis${query}`)
)

// this fetches an array of analysis statuses for
// this projectId by bandId and/or analysisTypeId.
export const fetchProjectAnalysisStatus = (projectId, query = '') => (
  fetchWrapper(`/projects/${projectId}/analysis-status${query}`)
)

// this fetches a rolled up analysis status for this project.
// meaning it does not specify a bandId or analysisTypeId, rather
// it is a computed, aggregated value representing their collective status.
export const fetchProjectAnalysisStatusRollup = projectId => (
  fetchWrapper(`/projects/${projectId}/analysis-status-rollup`)
)

export const fetchProjectByName = n => fetchWrapper(`/projects?projectName=${n}`)

export const fetchProjectCompanies = p => fetchWrapper(`/projects/${p}/companies`)

export const fetchProjectContacts = p => fetchWrapper(`/projects/${p}/contacts`)

export const fetchProjectFinalizationSettings = projectId => (
  fetchWrapper(`/projects/${projectId}/finalization-setting`)
)

export const fetchProjectInterferenceParameters = projectId => (
  fetchWrapper(`/projects/${projectId}/interference-project-parameters`)
)

export const fetchProjectPcnDatasheetPreview = projectId => (
  fetchWrapper(`/projects/${projectId}/pcn-datasheet-preview`)
)

export const fetchProjectPcnLetterPreview = (projectId, query) => (
  fetchWrapper(`/projects/${projectId}/pcn-email-preview${query}`)
)

export const fetchProjectsByQuery = q => fetchWrapper(`/projects${q}`)

export const submitClientDeliverablesJob = (projectId, query) => (
  postWrapper(`/projects/${projectId}/client-deliverables${query}`)
)

export const patchASRUpdatesToProject = (p, u) => (
  patchWrapper(`/projects/${p}/asr-updates`, u)
)

export const patchCallSignUpdatesToProject = (p, u) => (
  patchWrapper(`/projects/${p}/call-sign-updates`, u)
)

export const postASRCheck = (projectId, query) => postWrapper(`/projects/${projectId}/check/asr${query}`)

export const postASRCheckPrint = (projectId, query = '') => (
  postWrapper(`/projects/${projectId}/check/asr/print${query}`)
)

export const postCallSignCheck = (projectId, query) => (
  postWrapper(`/projects/${projectId}/check/call-sign${query}`)
)

export const postCreatePcnEmailFromTemplate = (projectId, body) => (
  postWrapper(`/projects/${projectId}/pcn-email-template`, body)
)

// NOTE - this posts JSON and not a CSV file!
// Posting the CSV file to be parsed into JSON
// is handled by postPathImportCSV()
export const postCSVPathImport = (projectId, pathsData) => (
  postWrapper(`/projects/${projectId}/csv-import`, pathsData)
)

export const postEarthStationInterferenceCaseReport = (projectId, query = '') => (
  postWrapper(`/projects/${projectId}/earth-station-interference-cases/export${query}`)
)

export const postProjectFinalization = projectId => postWrapper(`/projects/${projectId}/finalize`)

export const postProjectFinalizationAttachmentUpload = (projectId, formData) => (
  postFormDataWrapper(`/projects/${projectId}/files`, formData)
)

export const postProjectFinalizationManualExport = projectId => (
  postWrapper(`/projects/${projectId}/manual-export`)
)

export const postProjectInterferenceCaseReport = (projectId, query) => (
  postWrapper(`/projects/${projectId}/interference-case-report${query}`)
)

export const postProjectPathReportExport = (q = '') => (
  postWrapper(`/projects/export-data${q}`)
)

// NOTE - this endpoint can take a query:
// ?fileName=STRING
// but the UI has no need for this functionality currently.
// when fileName is not given, the backend will generate it as
// {PROJECT_NAME}_warnings_document_{YYYY_MM_DD_HH_mm_SS}.docx
export const postProjectWarningsJob = (projectId, query = '') => (
  postWrapper(`/projects/${projectId}/warnings${query}`)
)

export const postReplacePcnDatasheet = (projectId, body) => (
  postFormDataWrapper(`/projects/${projectId}/files/pcn-data-sheet`, body)
)

export const postRunProjectAnalysis = projectId => postWrapper(`/projects/${projectId}/analysis`)

export const postSetProjectToPending = projectId => postWrapper(`/projects/${projectId}/pending`)

export const postSplitProject = d => postWrapper('/projects/split', d)

export const putProjectFinalizationSettings = (projectId, body) => (
  putWrapper(`/projects/${projectId}/finalization-setting`, body)
)

export const putProjectSettings = (p, s) => putWrapper(`/projects/${p}`, s)

export const saveProjectToDB = p => postWrapper('/projects', p)

export const postCreateProtectionProject = query => postWrapper(`/projects/protection${query}`)

export const putProjectInterferenceParameters = (projectId, body) => (
  putWrapper(`/projects/${projectId}/interference-project-parameters`, body)
)

export const postResendPCN = projectId => postWrapper(`/projects/${projectId}/resend-email`)
